import React, { useMemo } from "react";

import * as yup from "yup";

import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { FormProvider, useForm } from "react-hook-form";

import Button from "react-bootstrap/Button";

import Input from "../../../../components/Input";

import { QUERY_CLIENT_PREFERENCES } from "../../../../config/graphql/query";
import { UPDATE_CLIENT_PREFERENCES } from "../../../../config/graphql/mutation";

import { useClientId } from "../hooks/useClient";

interface FieldValues {
  host: string;
  username: string;
  password: string;
}

const MinewIntegration = React.memo(() => {
  const { t } = useTranslation(["client", "common"]);

  const clientId = useClientId();

  const schema = useMemo(
    () =>
      yup.object().shape({
        host: yup.string().required(t("common:validation.required")),
        username: yup.string().required(t("common:validation.required")),
        password: yup.string().required(t("common:validation.required")),
      }),
    [t],
  );

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    shouldFocusError: false,
    shouldUnregister: true,
  });

  const { data, loading, error } = useQuery<{
    client: {
      preferences: {
        minewCredentials: {
          enabled: boolean;
          useDefault: boolean;
          host: string;
          username: string;
          password: string;
        };
      };
    };
  }>(QUERY_CLIENT_PREFERENCES, {
    skip: !clientId,
    variables: { id: clientId },
    fetchPolicy: "cache-and-network",
    onCompleted: ({ client }) => {
      console.log("Query completed, client data:", client);
      if (client?.preferences?.minewCredentials) {
        console.log(
          "Found minewCredentials:",
          client.preferences.minewCredentials,
        );
        methods.reset({
          host: client.preferences.minewCredentials.host || "",
          username: client.preferences.minewCredentials.username || "",
          password: client.preferences.minewCredentials.password || "",
        });
      } else {
        console.log("No minewCredentials found in response");
      }
    },
    onError: (error) => {
      console.error("Query error:", error);
    },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  console.log(
    "ClientId:",
    clientId,
    "Loading:",
    loading,
    "Error:",
    error,
    "Data:",
    data,
  );

  const [updatePreferences] = useMutation(UPDATE_CLIENT_PREFERENCES, {
    refetchQueries: [
      {
        query: QUERY_CLIENT_PREFERENCES,
        variables: { id: clientId },
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const onSubmit = React.useCallback(
    async (values: FieldValues) => {
      return updatePreferences({
        variables: {
          input: {
            id: clientId,
            preferences: {
              minewCredentials: {
                enabled: true,
                useDefault: false,
                host: values.host,
                username: values.username,
                password: values.password,
              },
            },
          },
        },
      })
        .then(() => {
          toast.success(t("common:success") as string);
        })
        .catch((error) => {
          toast.error(
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message,
          );
        });
    },
    [clientId, updatePreferences, t],
  );

  return (
    <div className="container-fluid">
      {loading && <div>Loading...</div>}
      {error && <div>Error: {error.message}</div>}
      <FormProvider {...methods}>
        <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col-lg-4 col-md-6 col-sm-12">
            <div className="form-group">
              <label htmlFor="host">
                {t("client:client.minew.form.host", "API endpoint URL")}
              </label>
              <Input name="host" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="username">
                {t("client:client.minew.form.username", "API username")}
              </label>
              <Input name="username" className="form-control" />
            </div>
            <div className="form-group">
              <label htmlFor="password">
                {t("client:client.minew.form.password", "API password")}
              </label>
              <Input name="password" className="form-control" type="password" />
            </div>
            <div className="form-group">
              <Button type="submit" variant="primary">
                {t("common:save", "CREATE")}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
});

export default MinewIntegration;
